import React, { useState, FunctionComponent, useContext, useEffect, CSSProperties } from 'react';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';
import { getWorldTitle, ModeRouteParams, Worlds } from '../mode/Mode';
import logos from '../../media/logos-flag.png';
import questionnaires from './questionnaires';
import Character from '../../components/character/Character';
import multipleOptionBackToLobby from '../../media/control-button-back-to-lobby.png';
import questionCard from '../../media/question-card.png';
import controlButtonOk from '../../media/control-button-ok.png';
import controlButtonSound from '../../media/control-sound.png';
import controlVideo from '../../media/control-video.png';
import controlArrowLeft from '../../media/control-arrow-left.png';
import controlArrowRight from '../../media/control-arrow-right.png';
import GameContext from '../../context/GameContext';
import { GroupGame, updatePlayerScoreInGroup } from '../group/Group';
import { auth } from '../../../services/firebase';
import Information from '../../components/common/Information';

interface QuestionnaireProps {
    groupGame?: GroupGame | null;
}

const Questionnaire: FunctionComponent<QuestionnaireProps> = ({ groupGame }) => {
    const history = useHistory();
    const routerParams = useParams<ModeRouteParams>();
    const { path } = useRouteMatch();

    const [showVideoPlayer, setShowVideoPlayer] = useState(false);

    const { playerName, singlePlayerGameState, handleMainSound, handleDialogSound, handleSetPlayerScore } =
        useContext(GameContext);

    const handleDialogPlay = (id: string) => {
        if (handleDialogSound) {
            handleMainSound('stop-background-music');

            const [playDialog] = handleDialogSound[id];
            playDialog();
        }
    };

    const handleVideoPlay = () => {
        handleMainSound('stop-background-music');
        setShowVideoPlayer(true);
    };

    const handleVideoStop = () => {
        handleMainSound('play-background-music');
        setShowVideoPlayer(false);
    };

    const isReviewing = path.includes('review');

    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [currentAnsweredIndex, setCurrentAnsweredIndex] = useState(-1);
    const [isAnswered, setIsAnswered] = useState(false);

    const currentQuestionnaire = getCurrentQuestionnaire(routerParams.worldId);
    const { text, options, correctIndex, cardImage, audio, video, copyright } =
        currentQuestionnaire[currentQuestionIndex];

    const handleOptionSelection = (optionIndex: number) => {
        setCurrentAnsweredIndex(optionIndex);
        setIsAnswered(true);

        if (optionIndex === correctIndex) {
            handleSetPlayerScore(singlePlayerGameState.score + 1);
            handleMainSound('play-success-effect');

            if (groupGame) {
                if (auth.currentUser?.uid)
                    updatePlayerScoreInGroup(routerParams.groupId, singlePlayerGameState.score, auth.currentUser.uid);
            }
        } else {
            handleMainSound('play-fail-effect');
        }
    };

    const handleContinueQuestionnaire = () => {
        setCurrentAnsweredIndex(-1);
        setIsAnswered(false);
        setCurrentQuestionIndex(currentQuestionIndex + 1);
    };

    const handleEndQuestionnaire = () => {
        if (groupGame) {
            history.push(`${process.env.PUBLIC_URL}/score/${routerParams.worldId}/${routerParams.groupId}`);
        } else {
            history.push(`${process.env.PUBLIC_URL}/score/${routerParams.worldId}`);
        }
    };

    const handleGoToNextQuestion = () => {
        handleMainSound('play-background-music');

        if (currentQuestionIndex + 1 === currentQuestionnaire.length) {
            setCurrentQuestionIndex(0);
        } else {
            setCurrentQuestionIndex(currentQuestionIndex + 1);
        }
    };

    const handleGoToPreviousQuestion = () => {
        if (currentQuestionIndex - 1 === -1) {
            setCurrentQuestionIndex(currentQuestionnaire.length - 1);
        } else {
            setCurrentQuestionIndex(currentQuestionIndex - 1);
        }
    };

    const handleGoBack = () => {
        history.push(`${process.env.PUBLIC_URL}/lobby`);
    };

    const incorrectAnswerStyle = (index: number): CSSProperties =>
        currentAnsweredIndex > 0 && currentAnsweredIndex === index && correctIndex !== index
            ? {
                  backgroundColor: 'tomato',
                  color: 'white',
              }
            : {};

    useEffect(() => {
        handleMainSound('level-down-background-music');

        return () => {
            if (handleDialogSound && audio) {
                const [, dialogControl] = handleDialogSound[audio];
                dialogControl.stop();
            }
        };
    }, [audio, handleDialogSound, handleMainSound]);

    useEffect(() => {
        handleSetPlayerScore(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            <div className="page-container page-questionnaire animate__animated animate__fadeIn">
                <img
                    src={getWorldTitle(routerParams)}
                    className="world-logo animate__animated animate__backInLeft"
                    alt="¡Bienvenido/a a la Cuenca!"
                />
                <img
                    src={logos}
                    className="common-logos animate__animated animate__slideInDown"
                    alt="Héroes de la Cuenca"
                />
                <div className="game-menu">
                    {groupGame ? (
                        <div className="group-score-board">
                            {groupGame.players.map((player) => (
                                <div className="group-player-box" key={player.uid} style={{ order: player.score }}>
                                    <div className="group-player-avatar">
                                        <Character seed={player.seed} />
                                    </div>
                                    <div className="group-player-info">
                                        <span className="group-player-name">
                                            {player.name.substring(0, 12)}
                                            {player.name.length > 12 && '...'}
                                        </span>
                                        <span className="group-player-score">{player.score} pts</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="character-mini-container animate__animated animate__bounceIn">
                            <Character name={playerName} />
                        </div>
                    )}

                    <img
                        src={multipleOptionBackToLobby}
                        className="game-menu-button-back animate__animated animate__backInUp"
                        alt="Regresar"
                        onClick={handleGoBack}
                    />
                </div>
                <div className="question-board">
                    {audio && (
                        <img
                            src={controlButtonSound}
                            className="question-sound animate__animated animate__heartBeat animate__infinite"
                            alt="Reproducir sonido"
                            onClick={() => handleDialogPlay(audio)}
                        />
                    )}
                    {video && (
                        <img
                            src={controlVideo}
                            className="question-video animate__animated animate__heartBeat animate__infinite"
                            alt="Reproducir video"
                            onClick={handleVideoPlay}
                        />
                    )}
                    <div className="question-cards">
                        <img
                            src={questionCard}
                            alt="¿Cuál crees que es la respuesta?"
                            className={`question-card animate__animated ${
                                !isAnswered && !isReviewing ? 'animate__pulse animate__infinite' : 'animate__zoomOut'
                            }`}
                        />
                        <img
                            src={cardImage}
                            alt="¿Cuál crees que es la respuesta?"
                            className={`question-card animate__animated ${
                                isAnswered || isReviewing ? 'animate__zoomIn' : 'animate__zoomOut'
                            }`}
                        />
                    </div>
                    <div className="question-content">
                        <p className="question-text">{text}</p>
                        <div className={`question-options ${(isAnswered || isReviewing) && 'answered'}`}>
                            {options.map((option, index) => (
                                <div
                                    key={index}
                                    className={`question-option ${
                                        (isAnswered || isReviewing) && correctIndex === index && 'correct'
                                    }`}
                                    style={incorrectAnswerStyle(index)}
                                    onClick={() => !isAnswered && handleOptionSelection(index)}
                                >
                                    {option}
                                </div>
                            ))}
                        </div>
                        {copyright && isAnswered && (
                            <div className="question-copyright">Crédito de la fotografía: {copyright}</div>
                        )}
                    </div>
                    <div className="question-buttons">
                        {isAnswered && !isReviewing && (
                            <img
                                src={controlButtonOk}
                                className="question-button-continue animate__custom_hover_scale"
                                alt="Continuar"
                                onClick={
                                    currentQuestionIndex + 1 < currentQuestionnaire.length
                                        ? handleContinueQuestionnaire
                                        : handleEndQuestionnaire
                                }
                            />
                        )}
                        {isReviewing && (
                            <>
                                <img
                                    src={controlArrowLeft}
                                    alt="Anterior"
                                    className="review-arrow left"
                                    onClick={handleGoToPreviousQuestion}
                                />
                                <img
                                    src={controlArrowRight}
                                    alt="Siguiente"
                                    className="review-arrow right"
                                    onClick={handleGoToNextQuestion}
                                />
                            </>
                        )}
                    </div>
                </div>
                <div className={`video-player ${showVideoPlayer ? 'show' : 'hide'}`}>
                    {showVideoPlayer && video && (
                        <iframe
                            width="560"
                            height="315"
                            src={`https://www.youtube.com/embed/${video}?autoplay=1&rel=0`}
                            title="YouTube video player"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        ></iframe>
                    )}
                    <div className="close-video" onClick={handleVideoStop}>
                        &mdash; Cerrar vídeo &mdash;
                    </div>
                </div>
            </div>
            <Information
                helpText={`Por favor elige una opción de las que están disponibles para cada pregunta. Si la respuesta es correcta ganarás un punto, sino ¡no te desanimes! puedes aprender viendo la imagen descubierta en la carta oculta. También puedes reproducir audios y vídeos educativos en algunas preguntas. Estas se presentan a manera de íconos en la parte superior izquierda y derecha del tablero de pregunta. Cuando respondas puedes hacer clic en el botón inferior de confirmación para continuar hasta acabar la ronda de esta Cuenca. ${
                    groupGame
                        ? 'En la parte izquierda de la pantalla podrás ver el listado de los jugadores que están en esta partida, cada acierto en las respuestas es un punto que permitirá ordenar las posiciones.'
                        : ''
                }`}
            />
        </>
    );
};

const getCurrentQuestionnaire = (worldId: Worlds) => {
    return questionnaires[worldId];
};

export default Questionnaire;
